import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData, selectTheme } from "../../themeSlice";
// import { processQuestions } from "../../questionSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import Switch from "react-switch";

const VALID_AUDIO_EXT = ["mp3", "m4a", "wav", "ogg"];
const SPLASH_SOUND_MAX_FILE_MB = 2;

const renameFileForUpload = (file) => {
  if (!file || !(file instanceof Blob)) return file;
  const original = file.name || "audio";
  const lastDot = original.lastIndexOf(".");
  const ext = lastDot >= 0 ? original.slice(lastDot) : "";
  const base = lastDot >= 0 ? original.slice(0, lastDot) : original;
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  const newName = `${id}-${base}${ext}`;
  return new File([file], newName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified,
  });
};


const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [isIntroModelOpen, setisIntroModelOpen] = useState(false);
  // bring in all four values from your theme slice exactly as before
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  // left‐side: rules
  const [rules, setRules] = useState([""]);

  // Basic theme details: Title, Step 1 Text, Step 2 Text
  const [title, setTitle] = useState("");
  const [step1Text, setStep1Text] = useState("");
  const [step2Text, setStep2Text] = useState("");
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [minutes, setMinutes] = useState("5");
  const [seconds, setSeconds] = useState("0");
  const [useTimeout, setUseTimeout] = useState(true);
  const [splashSoundName, setSplashSoundName] = useState("");
  const [splashSoundFile, setSplashSoundFile] = useState(null);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#000000",
    button_text_color: "#ffffff",
    leaderboard_color: "#000000",
    table_color1: "#000000",
    table_color2: "#ffffff",
  });

  useEffect(() => {
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    if (!source) return;
    setColors((prev) => ({
      text_color: source.text_color ?? prev.text_color,
      button_color: source.button_color ?? prev.button_color,
      button_text_color: source.button_text_color ?? prev.button_text_color,
      leaderboard_color: source.leaderboard_color ?? prev.leaderboard_color,
      table_color1: source.table_color1 ?? prev.table_color1,
      table_color2: source.table_color2 ?? prev.table_color2,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  // when theme data arrives, populate rules + basic theme details
  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setTitle(data.follow_text || "");
    setStep1Text(data.step1_text || "");
    setStep2Text(data.step2_text || "");
    setFeedbackTitle(data.feedback_title || "");

    // Load timer data
    const timerValue = data.timer || "05:00";
    const [timerMinutes, timerSeconds] = timerValue.split(':').map(Number);
    setMinutes(String(timerMinutes || 5));
    setSeconds(
      timerSeconds !== undefined && timerSeconds !== null && !Number.isNaN(timerSeconds)
        ? String(timerSeconds)
        : "0"
    );
    setUseTimeout(data?.use_timeout);
    setSplashSoundName(data?.splash_sound || data?.impact_sound || "");
  }, [data]);

  const splashSoundBlobSrc = React.useMemo(
    () => (splashSoundFile ? URL.createObjectURL(splashSoundFile) : ""),
    [splashSoundFile]
  );

  const splashSoundSrc = React.useMemo(() => {
    if (splashSoundBlobSrc) return splashSoundBlobSrc;
    if (!splashSoundName) return "";
    // If it's already an absolute URL, don't prepend S3_PATH.
    if (/^https?:\/\//i.test(String(splashSoundName))) return String(splashSoundName);
    const base = String(process.env.REACT_APP_S3_PATH || "").replace(/\/+$/, "");
    const raw = String(splashSoundName || "").replace(/^\/+/, "");
    return base ? `${base}/${raw}` : raw;
  }, [splashSoundBlobSrc, splashSoundName]);

  useEffect(() => {
    if (!splashSoundBlobSrc) return undefined;
    return () => URL.revokeObjectURL(splashSoundBlobSrc);
  }, [splashSoundBlobSrc]);

  const validateSplashSound = (file) => {
    const ext = (file?.name?.split(".").pop() || "").toLowerCase();
    if (!VALID_AUDIO_EXT.includes(ext)) {
      return `Unsupported format for ${file.name}. Use MP3, M4A, WAV, or OGG.`;
    }
    if (file.size > SPLASH_SOUND_MAX_FILE_MB * 1024 * 1024) {
      return `${file.name} exceeds ${SPLASH_SOUND_MAX_FILE_MB}MB`;
    }
    return null;
  };

  const onPickSplashSound = (file) => {
    if (!file) return;
    const error = validateSplashSound(file);
    if (error) {
      Swal.fire("Invalid file", error, "error");
      return;
    }
    const renamed = renameFileForUpload(file);
    setSplashSoundFile(renamed);
    setSplashSoundName(renamed.name);
  };

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleTitleChange = (val) => {
    if (val.length > 50) {
      Swal.fire("Limit Exceeded", "Title can be at most 300 characters. Extra text has been trimmed.", "warning");
      val = val.slice(0, 50);
    }
    setTitle(val);
  };
  

  const handleMinutesChange = (e) => {
    const { value } = e.target;
    if (value === "") {
      setMinutes("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    setMinutes(String(parsed));
  };

  const handleMinutesBlur = () => {
    const trimmed = (minutes ?? "").trim();
    if (trimmed === "") {
      Swal.fire(
        "Minutes required",
        "Minutes cannot be empty. Enter at least 1 minute for the countdown timer.",
        "warning"
      );
      setMinutes("1");
      return;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      Swal.fire(
        "Invalid minutes",
        "Minutes must be a number at least 1.",
        "warning"
      );
      setMinutes("1");
      return;
    }
    setMinutes(String(parsed));
  };

  const handleSecondsChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    if (digitsOnly === "") {
      setSeconds("");
      return;
    }
    const parsed = Number.parseInt(digitsOnly, 10);
    if (Number.isNaN(parsed)) return;
    setSeconds(String(parsed));
  };

  const handleSecondsBlur = () => {
    const trimmed = (seconds ?? "").trim();
    if (trimmed === "") {
      return;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed)) {
      setSeconds("");
      return;
    }
    setSeconds(String(Math.max(0, Math.min(59, parsed))));
  };

  const handleTimerToggle = (checked) => {
    setUseTimeout(checked);
  };

  const normalizedMinutes = Math.max(
    1,
    Number.parseInt(minutes, 10) || 1
  );
  const normalizedSeconds = Math.min(
    59,
    Math.max(0, Number.parseInt(seconds, 10) || 0)
  );
  const timerDisplay = `${String(normalizedMinutes).padStart(2, "0")}:${String(normalizedSeconds).padStart(2, "0")}`;

  // --- submit all ---
  const handleSubmit = async () => {
    const trimmedRules = rules.map((r) => (r ?? "").trim());

    // Ensure there is at least one rule and no rule row is left blank.
    if (trimmedRules.length === 0 || trimmedRules.every((r) => !r)) {
      await Swal.fire(
        "At Least One Rule Required",
        "Please enter at least one rule.",
        "warning"
      );
      return;
    }

    const firstEmptyIndex = trimmedRules.findIndex((r) => !r);
    if (firstEmptyIndex !== -1) {
      await Swal.fire(
        "Rule Text Required",
        `Rule #${firstEmptyIndex + 1} cannot be empty.`,
        "warning"
      );
      return;
    }

    if (useTimeout) {
      const mTrimmed = (minutes ?? "").trim();
      const parsedMinutes = Number.parseInt(mTrimmed, 10);
      if (
        mTrimmed === "" ||
        !Number.isFinite(parsedMinutes) ||
        parsedMinutes < 1
      ) {
        await Swal.fire(
          "Minutes required",
          "Minutes cannot be empty. Enter at least 1 minute for the countdown timer.",
          "warning"
        );
        return;
      }
    }

    const payload = {
      data: {
        rules: trimmedRules,
        follow_text: title,
        step1_text: step1Text,
        step2_text: step2Text,
        feedback_title: feedbackTitle,
        // Persist selected splash sound name even before/without upload.
        // (Upload step will overwrite with the final stored filename if needed.)
        splash_sound: splashSoundName || "",
        // Backward compatibility: game historically reads `impact_sound`.
        impact_sound: splashSoundName || data?.impact_sound || "",
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_text_color: colors.button_text_color,
        leaderboard_color: colors.leaderboard_color,
        table_color1: colors.table_color1,
        table_color2: colors.table_color2,
        timer: timerDisplay,
        use_timeout: useTimeout ? "true" : "false",
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });

      if (splashSoundFile) {
        const formData = new FormData();
        formData.append("themeName", currentTheme);
        formData.append("extraData", JSON.stringify({ splash_sound: splashSoundName }));
        formData.append("splash_sound", splashSoundFile);
        await uploadThemeMedia({ formData, token: adminToken });
      }

      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/themeUpdate"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />


          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Details
                </div>
                <div className="form-group-v1">
                  <label>Title</label>
                  <textarea
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Title"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={50}
                  />
                  <div className="code">* Maximum 50 characters.</div>
                </div>
                <div className="form-group-v1">
                  <label>Feedback Page Title</label>
                  <textarea
                    value={feedbackTitle}
                    onChange={(e) => setFeedbackTitle(e.target.value.slice(0, 120))}
                    placeholder="Feedback page title/question"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={120}
                  />
                  <div className="code">* Shown on feedback page, max 120 characters.</div>
                </div>
                <div className="form-group-v1">
                  <label>Splash Sound</label>
                  <input
                    type="file"
                    accept=".mp3,.m4a,.wav,.ogg,audio/*"
                    onChange={(e) => onPickSplashSound(e.target.files?.[0])}
                  />
                  <div className="code">* Allowed: MP3/M4A/WAV/OGG, max 2MB.</div>
                  {/* Hide filename; audio controls below provide the preview */}
                  {splashSoundSrc ? (
                    <audio
                      key={splashSoundSrc}
                      controls
                      controlsList="nodownload"
                      preload="metadata"
                      src={splashSoundSrc}
                    >
                      Your browser does not support the audio element.
                    </audio>
                  ) : null}
                </div>
                <div className="form-group-v1">
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>Countdown timer on game page</span>
                    {/* Toggle hidden as requested; timer fields below remain visible */}
                    <span style={{ display: "none" }}>
                      <Switch
                        id="tu-use-timer"
                        checked={useTimeout}
                        onChange={handleTimerToggle}
                        onColor="#4caf50"
                        offColor="#ccc"
                        aria-checked={useTimeout}
                      />
                    </span>
                  </label>
                  {/* <p
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      margin: "5px 0 15px 0",
                      lineHeight: "1.4",
                    }}
                  >
                    When off, players have no time limit; values below are still saved for
                    if you turn the timer on later.
                  </p> */}
                  <div className="points-inline-row">
                    <div style={{ flex: 1 }}>
                      <label htmlFor="tu-minutes">Minutes</label>
                      <input
                        id="tu-minutes"
                        type="number"
                        min={1}
                        value={minutes}
                        onChange={handleMinutesChange}
                        onBlur={handleMinutesBlur}
                        className="points-inline-input"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="tu-seconds">Seconds</label>
                      <input
                        id="tu-seconds"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={seconds}
                        onChange={handleSecondsChange}
                        onBlur={handleSecondsBlur}
                        className="points-inline-input"
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", paddingLeft: "10px" }}>
                      <span style={{ fontSize: "16px", fontWeight: "600", color: "#444" }}>
                        {timerDisplay}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 6 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_text_color}
                          onChange={handleColorChange("button_text_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
